/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/

// 顺序即切换器里的显示顺序:日本語第一、한국어第二。
export const INTERFACE_LANGUAGE_OPTIONS = [
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '简体中文' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'ru', label: 'Русский' },
  { code: 'vi', label: 'Tiếng Việt' },
] as const

// 按站点开关的附加语言:仅当后端 /api/status 的 extra_interface_langs 含其
// code 时才进切换器(如 SG=omniapi 开繁体中文)。'tw' 是内部码——避开
// i18next load:'languageOnly' 会把 zh-TW 折叠回 zh;对外 html lang 由
// config.ts 映射成 zh-TW。
export const EXTRA_INTERFACE_LANGUAGE_OPTIONS = [
  { code: 'tw', label: '繁體中文' },
] as const

export function getInterfaceLanguageOptions(extraLangs?: readonly string[]) {
  const extra = EXTRA_INTERFACE_LANGUAGE_OPTIONS.filter((o) =>
    extraLangs?.includes(o.code)
  )
  return [...INTERFACE_LANGUAGE_OPTIONS, ...extra]
}

export type InterfaceLanguageCode =
  (typeof INTERFACE_LANGUAGE_OPTIONS)[number]['code']

export function normalizeInterfaceLanguage(value?: string | null): string {
  if (!value) return 'en'

  const normalized = value.trim().replace(/_/g, '-').toLowerCase()
  // 繁体(台湾/香港/Hant)归内部码 tw;需在 startsWith('zh') 之前判断
  if (
    normalized === 'tw' ||
    normalized.startsWith('zh-tw') ||
    normalized.startsWith('zh-hant') ||
    normalized.startsWith('zh-hk')
  )
    return 'tw'
  if (normalized.startsWith('zh')) return 'zh'

  if (INTERFACE_LANGUAGE_OPTIONS.some((lang) => lang.code === normalized)) {
    return normalized
  }

  // 系统语言常带地区后缀(如 ko-KR / vi-VN / ja-JP),剥离后再匹配一次,
  // 保证「跟随系统语言」在带地区码的环境下也能命中。
  const base = normalized.split('-')[0]
  return INTERFACE_LANGUAGE_OPTIONS.some((lang) => lang.code === base)
    ? base
    : 'en'
}

export function toIntlLocale(value?: string | null): string | undefined {
  if (!value) return undefined
  switch (value) {
    case 'zhCN':
      return 'zh-CN'
    case 'zhTW':
      return 'zh-TW'
    default:
      break
  }
  try {
    return Intl.getCanonicalLocales(value)[0]
  } catch {
    return undefined
  }
}
